// ============================================================
// LevelObjects — Platforms, Hazards, Goals, NPCs
// ============================================================
import * as PIXI from 'pixi.js';
import { PhysicsBody } from '../physics/PhysicsBody.js';
import { COLOR, Z } from '../constants.js';
import { sound } from '../systems/SoundEngine.js';

// ────────────────────────────────────────────────────────────
// Platform
// ────────────────────────────────────────────────────────────
export class Platform {
  /**
   * @param {object} spec    - { x, y, w, h, material, color, isStatic, moving }
   * @param {PIXI.Container} container
   */
  constructor(spec, container) {
    this.spec = spec;
    this.container = container;
    this.alive = true;
    this._moveT = 0;
    this._originX = spec.x;
    this._originY = spec.y;

    // Physics
    this.body = new PhysicsBody({
      x: spec.x, y: spec.y,
      width: spec.w, height: spec.h,
      isStatic: true,
      material: spec.material ?? 'default',
    });
    this.body.entity = this;

    // Graphics
    this.gfx = new PIXI.Graphics();
    this.gfx.zIndex = Z.platforms;
    container.addChild(this.gfx);
    this._draw();
    this._sync();
  }

  _draw() {
    const { w, h, color, material } = this.spec;
    const c = color ?? 0x7F8C8D;

    this.gfx.clear();
    // Shadow
    this.gfx.rect(-w/2 + 3, -h/2 + 3, w, h).fill({ color: 0x000000, alpha: 0.18 });
    // Main body
    this.gfx.rect(-w/2, -h/2, w, h).fill({ color: c });
    // Top highlight
    this.gfx.rect(-w/2, -h/2, w, 4).fill({ color: 0xFFFFFF, alpha: 0.25 });

    // Material texture stripes
    if (material === 'sand') {
      for (let i = 0; i < Math.floor(w / 20); i++) {
        this.gfx.rect(-w/2 + i*20 + 5, -h/2 + 5, 3, h - 10)
          .fill({ color: 0xF0C050, alpha: 0.3 });
      }
    }
    if (material === 'stone') {
      this.gfx.rect(-w/2 + 2, -h/2 + 2, w/2 - 4, h/2 - 4).stroke({ color: 0x000000, alpha: 0.12, width: 1 });
      this.gfx.rect(+2, -h/2 + 2, w/2 - 4, h/2 - 4).stroke({ color: 0x000000, alpha: 0.12, width: 1 });
    }
    if (material === 'ice') {
      this.gfx.rect(-w/2 + 4, -h/2 + 3, w - 8, 4).fill({ color: 0xFFFFFF, alpha: 0.5 });
    }
    if (material === 'gold') {
      // Shimmering gold
      this.gfx.rect(-w/2, -h/2, w, 6).fill({ color: 0xFFEC40, alpha: 0.8 });
    }
    if (material === 'grass') {
      this.gfx.rect(-w/2, -h/2, w, 8).fill({ color: 0x66BB6A });
    }
    if (material === 'cloud') {
      // Fluffy outline
      this.gfx.roundRect(-w/2, -h/2, w, h, 20).stroke({ color: 0x9FCCEF, alpha: 0.8, width: 3 });
    }
    if (material === 'coral') {
      for (let i = 0; i < Math.floor(w/30); i++) {
        this.gfx.circle(-w/2 + i*30 + 15, -h/2, 10).fill({ color: 0xFF8A65, alpha: 0.7 });
      }
    }
    if (material === 'lava' || this.spec.type === 'lava') {
      this.gfx.rect(-w/2, -h/2, w, h).fill({ color: 0xFF4500 });
      // Animated glow strips drawn at runtime
    }
  }

  update(dt) {
    const { moving } = this.spec;
    if (!moving) return;

    this._moveT += dt;
    const half = moving.range / 2;
    const offset = Math.sin(this._moveT * moving.speed / moving.range * Math.PI) * half;

    if (moving.axis === 'x') {
      this.body.x = this._originX + offset;
    } else {
      this.body.y = this._originY + offset;
    }
    this._sync();
  }

  _sync() {
    this.gfx.x = this.body.x;
    this.gfx.y = this.body.y;
  }

  destroy() {
    this.alive = false;
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }
}

// ────────────────────────────────────────────────────────────
// Hazard
// ────────────────────────────────────────────────────────────
export class Hazard {
  constructor(spec, container) {
    this.spec = spec;
    this.alive = true;

    const typeColors = {
      lava:      0xFF4500,
      spike:     0x607D8B,
      jellyfish: 0xFF80AB,
      boulder:   0x546E7A,
    };

    this.body = new PhysicsBody({
      x: spec.x, y: spec.y,
      width: spec.w, height: spec.h,
      isStatic: true,
      isTrigger: true,
    });
    this.body.entity = this;
    this.body.onTrigger = (other) => {
      if (other.entity?.hurt) other.entity.hurt();
    };

    this.gfx = new PIXI.Graphics();
    this.gfx.zIndex = Z.hazards;
    container.addChild(this.gfx);
    this._draw(typeColors[spec.type] ?? 0xFF0000);
    this._sync();

    this._time = 0;
  }

  _draw(color) {
    const { w, h, type } = this.spec;
    this.gfx.clear();

    if (type === 'spike') {
      const count = Math.floor(w / 20);
      for (let i = 0; i < count; i++) {
        const sx = -w/2 + i * 20 + 10;
        this.gfx.poly([sx-8, h/2, sx+8, h/2, sx, -h/2]).fill({ color });
      }
    } else if (type === 'jellyfish') {
      this.gfx.ellipse(0, 0, w/2, h/2).fill({ color, alpha: 0.7 });
      for (let i = 0; i < 5; i++) {
        const tx = (i - 2) * (w / 5);
        this.gfx.moveTo(tx, h/2).lineTo(tx, h/2 + 20)
          .stroke({ color, alpha: 0.5, width: 2 });
      }
    } else {
      // Lava / generic
      this.gfx.rect(-w/2, -h/2, w, h).fill({ color, alpha: 0.9 });
      // Bubble details for lava
      if (type === 'lava') {
        for (let i = 0; i < 4; i++) {
          this.gfx.circle(-w/2 + w * (i + 0.5) / 4, -h/2 + 8, 6)
            .fill({ color: 0xFFAA00, alpha: 0.8 });
        }
      }
    }
  }

  update(dt) {
    this._time += dt;
    // Jellyfish bob
    if (this.spec.type === 'jellyfish') {
      this.body.y = this.spec.y + Math.sin(this._time * 2) * 20;
      this._sync();
    }
    // Lava pulse
    if (this.spec.type === 'lava') {
      this.gfx.alpha = 0.8 + Math.sin(this._time * 4) * 0.2;
    }
  }

  _sync() {
    this.gfx.x = this.body.x;
    this.gfx.y = this.body.y;
  }

  destroy() {
    this.alive = false;
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }
}

// ────────────────────────────────────────────────────────────
// Goal
// ────────────────────────────────────────────────────────────
export class Goal {
  constructor(spec, container, onReach) {
    this.spec     = spec;
    this.reached  = false;
    this._time    = 0;
    this._cb      = onReach;

    this.body = new PhysicsBody({
      x: spec.x, y: spec.y,
      width: 60, height: 60,
      isStatic: true,
      isTrigger: true,
    });
    this.body.entity = this;
    this.body.onTrigger = (other) => {
      if (!this.reached && other.entity?.constructor?.name === 'TankCharacter') {
        this.reached = true;
        this._cb?.();
      }
    };

    this.gfx = new PIXI.Container();
    this.gfx.zIndex = Z.spawned + 5;
    container.addChild(this.gfx);

    // Star graphic
    const star = new PIXI.Graphics();
    this._drawStar(star, 30, 0xFFD700);
    this.gfx.addChild(star);
    this._starGfx = star;

    // Glow ring
    const glow = new PIXI.Graphics();
    glow.circle(0, 0, 40).fill({ color: 0xFFFF80, alpha: 0.3 });
    this.gfx.addChildAt(glow, 0);
    this._glowGfx = glow;

    this.gfx.x = spec.x;
    this.gfx.y = spec.y;
  }

  _drawStar(g, r, color) {
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const angle = (i * Math.PI) / 5 - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r * 0.4;
      pts.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    g.poly(pts).fill({ color });
    g.poly(pts).stroke({ color: 0xFFFFFF, alpha: 0.5, width: 2 });
  }

  update(dt) {
    this._time += dt;
    this._starGfx.rotation = this._time * 1.2;
    this._starGfx.scale.set(0.9 + Math.sin(this._time * 3) * 0.1);
    this._glowGfx.alpha = 0.2 + Math.sin(this._time * 2) * 0.15;
  }

  destroy() {
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }
}

// ────────────────────────────────────────────────────────────
// Collectible (pearls, gems)
// ────────────────────────────────────────────────────────────
export class Collectible {
  constructor(spec, container, onCollect) {
    this.spec    = spec;
    this.alive   = true;
    this._time   = 0;
    this._cb     = onCollect;

    const typeConfig = {
      pearl: { color: 0xF5F5F5, r: 15, score: 200 },
      gem:   { color: 0x00BCD4, r: 14, score: 100 },
    };
    const tc = typeConfig[spec.type] ?? typeConfig.gem;

    this.body = new PhysicsBody({
      x: spec.x, y: spec.y,
      width: tc.r * 2, height: tc.r * 2,
      isStatic: true,
      isTrigger: true,
    });
    this.body.entity = this;
    this.body.onTrigger = (other) => {
      if (this.alive && other.entity?.constructor?.name === 'TankCharacter') {
        this.alive = false;
        this._cb?.(tc.score, spec);
        this.destroy();
      }
    };

    this.gfx = new PIXI.Container();
    this.gfx.zIndex = Z.spawned + 3;
    container.addChild(this.gfx);

    const g = new PIXI.Graphics();
    g.circle(0, 0, tc.r).fill({ color: tc.color });
    g.circle(-tc.r * 0.3, -tc.r * 0.3, tc.r * 0.25).fill({ color: 0xFFFFFF, alpha: 0.5 });
    this.gfx.addChild(g);

    this.gfx.x = spec.x;
    this.gfx.y = spec.y;
  }

  update(dt) {
    this._time += dt;
    this.gfx.y = this.spec.y + Math.sin(this._time * 2) * 8;
    this.gfx.rotation = this._time * 0.8;
    this.body.y = this.gfx.y;
  }

  destroy() {
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }
}

// ────────────────────────────────────────────────────────────
// NPC dialogue bubble
// ────────────────────────────────────────────────────────────
export class DialogNPC {
  constructor(spec, container) {
    this.spec       = spec;
    this._dialogIdx = 0;
    this._talking   = false;
    this._time      = 0;
    this.container  = container;

    // Simple NPC sprite
    this.gfx = new PIXI.Container();
    this.gfx.zIndex = Z.spawned + 2;
    container.addChild(this.gfx);

    const body = new PIXI.Graphics();
    body.circle(0, 0, 22).fill({ color: 0x4CAF50 });
    body.circle(0, 0, 22).stroke({ color: 0x2E7D32, width: 3 });
    // Shell pattern
    body.circle(0, 0, 13).fill({ color: 0x8D6E63, alpha: 0.8 });
    const face = new PIXI.Text({ text: '🐢', style: { fontSize: 28 } });
    face.anchor.set(0.5, 0.55);
    this.gfx.addChild(body);
    this.gfx.addChild(face);

    this.gfx.x = spec.x;
    this.gfx.y = spec.y;

    // Interaction radius
    this.body = new PhysicsBody({
      x: spec.x, y: spec.y,
      width: 120, height: 100,
      isStatic: true, isTrigger: true,
    });

    // Dialogue bubble (hidden initially)
    this._bubble = null;
  }

  showDialog() {
    if (this._talking) return;
    this._talking = true;
    this._showBubble(this.spec.dialog[this._dialogIdx]);
    this._dialogIdx = (this._dialogIdx + 1) % this.spec.dialog.length;
    sound.npcTalk();
    setTimeout(() => {
      this._talking = false;
      this._hideBubble();
    }, 4000);
  }

  _showBubble(text) {
    this._hideBubble();
    const style = new PIXI.TextStyle({
      fontSize: 15,
      fill: 0x1A1A2E,
      wordWrap: true,
      wordWrapWidth: 240,
      align: 'center',
    });
    const t = new PIXI.Text({ text, style });
    t.anchor.set(0.5, 1);

    const pad = 12;
    const bw = t.width + pad * 2;
    const bh = t.height + pad * 2;
    const bg = new PIXI.Graphics();
    bg.roundRect(-bw/2, -bh, bw, bh, 10).fill({ color: 0xFFFDE7 });
    bg.roundRect(-bw/2, -bh, bw, bh, 10).stroke({ color: 0xFFC107, width: 2 });
    // Tail
    bg.poly([-8, 0, 8, 0, 0, 12]).fill({ color: 0xFFFDE7 });

    const bubble = new PIXI.Container();
    bubble.addChild(bg);
    bubble.addChild(t);
    bubble.x = this.gfx.x;
    bubble.y = this.gfx.y - 30;
    bubble.zIndex = Z.ui - 1;
    this.container.addChild(bubble);
    this._bubble = bubble;
  }

  _hideBubble() {
    if (this._bubble) {
      if (this._bubble.parent) this._bubble.parent.removeChild(this._bubble);
      this._bubble.destroy();
      this._bubble = null;
    }
  }

  update(dt) {
    this._time += dt;
    // Gentle bob
    this.gfx.y = this.spec.y + Math.sin(this._time * 1.5) * 5;
  }

  destroy() {
    this._hideBubble();
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }
}
